import { useState, type FormEvent } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Button, Card, Input } from '@clari/ui'
import { AuthLayout } from '@/components/AuthLayout'
import { ErrorBanner } from '@/components/ErrorBanner'
import { ApiError } from '@/lib/api'
import { apiErrorMessage } from '@/lib/api-error'
import { authApi } from '@/lib/auth-api'
import { useAuthStore } from '@/stores/authStore'
import { useSessionStore } from '@/stores/sessionStore'

export function Login() {
  const { t } = useTranslation('auth')
  const navigate = useNavigate()
  const location = useLocation()
  const setSession = useAuthStore((s) => s.setSession)
  const resetSession = useSessionStore((s) => s.reset)

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [twoFactorRequired, setTwoFactorRequired] = useState(false)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setLoading(true)
    try {
      const res = await authApi.login(email, password, twoFactorRequired ? code : undefined)
      if ('two_factor_required' in res) {
        setTwoFactorRequired(true)
        return
      }
      setSession({ user: res.user, accessToken: res.access_token, refreshToken: res.refresh_token })
      resetSession()
      const from = (location.state as { from?: string } | null)?.from ?? '/app'
      navigate(from, { replace: true })
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? t('auth.invalid_credentials', { ns: 'errors' })
          : apiErrorMessage(err),
      )
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Card>
        <h1 className="text-2xl font-semibold">{t('signin.title')}</h1>
        <p className="mt-1 text-text-muted">{t('signin.subtitle')}</p>

        <form className="mt-6 flex flex-col gap-4" onSubmit={onSubmit}>
          <Input
            label={t('signin.email_label')}
            type="email"
            autoComplete="email"
            value={email}
            placeholder={t('signin.email_placeholder')}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={twoFactorRequired}
          />
          <Input
            label={t('signin.password_label')}
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
            disabled={twoFactorRequired}
          />

          {twoFactorRequired ? (
            <Input
              label={t('signin.twofa_code_label')}
              inputMode="numeric"
              autoComplete="one-time-code"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              hint={t('signin.twofa_subtitle')}
              required
            />
          ) : null}

          {error ? <ErrorBanner message={error} /> : null}

          <Button type="submit" disabled={loading}>
            {twoFactorRequired ? t('signin.twofa_submit') : t('signin.submit')}
          </Button>
        </form>

        <div className="mt-4 flex flex-col gap-2 text-sm">
          <Link to="/forgot-password" className="text-text-muted hover:text-text">
            {t('signin.forgot_password')}
          </Link>
          <span className="text-text-muted">
            {t('signin.no_account')}{' '}
            <Link to="/signup" className="font-medium text-text underline">
              {t('signin.signup_link')}
            </Link>
          </span>
        </div>
      </Card>
    </AuthLayout>
  )
}
